import { ChevronLeft, ChevronRight, Eye, FilePlus2 } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { PDF_SAMPLES, type PdfSample, findPdfSampleForCountry } from "@/lib/pdf-samples";
import { cn } from "@/lib/utils";

interface PdfSampleCarouselProps {
  title?: string;
  subtitle?: string;
  className?: string;
  limit?: number;
  selectedCountry?: string;
  density?: "compact" | "prominent";
}

export function PdfSampleCarousel({
  title = "PDF layout previews",
  subtitle = "Some countries have special invoice formats. Choose a generated PDF preview for a standard layout or a supported special format.",
  className,
  limit,
  selectedCountry,
  density = "compact",
}: PdfSampleCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [previewSample, setPreviewSample] = useState<PdfSample | null>(null);
  const [startSample, setStartSample] = useState<PdfSample | null>(null);
  const selected = selectedCountry?.toUpperCase();
  const samples = getVisiblePdfSamples(limit, selected);
  const prominent = density === "prominent";

  function scrollByCard(direction: -1 | 1) {
    scrollRef.current?.scrollBy({ left: direction * (prominent ? 360 : 300), behavior: "smooth" });
  }

  return (
    <section
      className={cn(
        "space-y-3",
        prominent && "rounded-lg border border-border bg-surface p-4",
        className,
      )}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-secondary">{subtitle}</p>
          <p className="mt-2 text-xs text-tertiary">
            {samples.length} PDF previews · Germany XRechnung/ZUGFeRD, France Factur-X, and one
            shared standard layout
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 px-0"
            aria-label="Previous samples"
            onClick={() => scrollByCard(-1)}
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 px-0"
            aria-label="Next samples"
            onClick={() => scrollByCard(1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex snap-x gap-3 overflow-x-auto pb-2 pr-2"
        aria-label="PDF layout samples"
      >
        {samples.map((sample) => (
          <PdfSampleCard
            key={sample.id}
            sample={sample}
            selected={!!selected && sample.sellerCountry === selected}
            prominent={prominent}
            onPreview={() => setPreviewSample(sample)}
            onStart={() => setStartSample(sample)}
          />
        ))}
      </div>

      <Dialog
        open={!!previewSample}
        onClose={() => setPreviewSample(null)}
        title={previewSample ? previewSample.formName : "PDF sample"}
        description={previewSample?.kind}
        size="xl"
      >
        {previewSample && (
          <div className="space-y-4">
            <div className="mx-auto w-full max-w-[920px] rounded-md bg-white p-3 shadow-sm">
              <img
                src={previewSample.imageSrc}
                alt={`${sampleAccessibleName(previewSample)} preview`}
                className="w-full rounded border border-stone-200 object-contain object-top"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-secondary">{previewSample.kind}</p>
              <p className="text-xs text-tertiary">
                Use the Start button below the sample to create.
              </p>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!startSample}
        onClose={() => setStartSample(null)}
        title="Choose how to create"
        description={startSample?.formName}
        size="lg"
      >
        {startSample && (
          <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
            <div className="overflow-hidden rounded-md border border-stone-200 bg-white">
              <img
                src={startSample.imageSrc}
                alt={`${sampleAccessibleName(startSample)} start preview`}
                className="aspect-[210/297] w-full object-cover object-top"
              />
            </div>
            <div className="space-y-3">
              <StartModeLink
                to={withEditorMode(startSample.startPath, "inline")}
                title="Visual inline editor"
                description="Edit directly on an invoice-shaped preview. Best for quick one-at-a-time invoices."
                meta="Uses the same country layout and PDF/XML output engine."
              />
              <StartModeLink
                to={withEditorMode(startSample.startPath, "form")}
                title="Structured form"
                description="Use guided fields for VAT, country-specific invoice details, and XML-ready outputs."
                meta="Recommended for Germany, France, Japan, Korea, and detailed tax cases."
              />
              <div className="rounded-md border border-border bg-elevated px-3 py-2 text-xs text-secondary">
                Some countries have special invoice formats. Germany XRechnung/ZUGFeRD and France
                Factur-X stay available when the seller country requires them. Bulk spreadsheet
                generation is a premium tool from the Bulk import screen.
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
}

export function getVisiblePdfSamples(limit?: number, selectedCountry?: string): PdfSample[] {
  if (!limit || limit >= PDF_SAMPLES.length) return PDF_SAMPLES;
  if (limit <= 0) return [];

  const samples = PDF_SAMPLES.slice(0, limit);
  const selected = selectedCountry?.toUpperCase();
  if (!selected || samples.some((sample) => sample.sellerCountry === selected)) return samples;

  const selectedSample = findPdfSampleForCountry(selected);
  if (!selectedSample) return samples;
  if (samples.some((sample) => sample.id === selectedSample.id)) return samples;

  return [...samples.slice(0, Math.max(limit - 1, 0)), selectedSample];
}

function PdfSampleCard({
  sample,
  selected,
  prominent,
  onPreview,
  onStart,
}: {
  sample: PdfSample;
  selected: boolean;
  prominent: boolean;
  onPreview: () => void;
  onStart: () => void;
}) {
  const featured = isFeaturedSample(sample.id);
  return (
    <article
      className={cn(
        "shrink-0 snap-start overflow-hidden rounded-lg border bg-surface",
        prominent ? "w-[260px] sm:w-[300px]" : "w-[180px] sm:w-[240px]",
        selected ? "border-accent ring-2 ring-accent/35" : "border-border",
      )}
    >
      <div>
        <div className="bg-elevated px-3 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-xs text-tertiary">{sample.kind}</p>
              <h3 className="mt-0.5 truncate text-sm font-semibold">{sample.formName}</h3>
            </div>
            <span
              className={cn(
                "rounded border px-1.5 py-0.5 text-[10px]",
                selected
                  ? "border-accent bg-accent text-accent-fg"
                  : featured
                    ? "border-border bg-elevated text-primary"
                    : "border-border bg-surface text-secondary",
              )}
            >
              {selected ? "Selected" : featured ? "Featured" : "PDF"}
            </span>
          </div>
        </div>

        <div className="relative bg-white p-2">
          <button
            type="button"
            onClick={onPreview}
            className="group block w-full text-left"
            aria-label={`Preview ${sampleAccessibleName(sample)}`}
          >
            <img
              src={sample.imageSrc}
              alt={sampleAccessibleName(sample)}
              className="aspect-[210/297] w-full rounded border border-stone-200 object-cover object-top shadow-sm"
              loading="lazy"
            />
            <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white/95 px-2 py-1 text-xs font-medium text-stone-700 shadow-sm transition-colors group-hover:bg-stone-100">
              <Eye size={15} />
              Preview
            </span>
          </button>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
          <p className="min-w-0 truncate text-xs text-secondary">{sample.kind}</p>
          <button
            type="button"
            onClick={onStart}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent hover:text-accent-hover"
          >
            <FilePlus2 size={13} />
            Start
          </button>
        </div>
      </div>
    </article>
  );
}

function StartModeLink({
  to,
  title,
  description,
  meta,
}: {
  to: string;
  title: string;
  description: string;
  meta: string;
}) {
  return (
    <Link
      to={to}
      className="block rounded-md border border-border bg-surface p-3 text-left transition-colors hover:border-accent/70 hover:bg-elevated"
    >
      <p className="text-sm font-semibold text-primary">{title}</p>
      <p className="mt-1 text-sm leading-6 text-secondary">{description}</p>
      <p className="mt-2 text-xs text-tertiary">{meta}</p>
    </Link>
  );
}

function withEditorMode(path: string, mode: "inline" | "form"): string {
  const [basePath, hash = ""] = path.split("#");
  const [pathname, query = ""] = basePath.split("?");
  const search = new URLSearchParams(query);
  const sample = PDF_SAMPLES.find((item) => item.startPath === path);
  if (sample) search.set("sample", sample.id);
  search.set("editor", mode);
  const nextQuery = search.toString();
  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}${hash ? `#${hash}` : ""}`;
}

function isFeaturedSample(id: string): boolean {
  return id === "de-rechnung" || id === "fr-facture" || id === "jp-qualified-invoice";
}

function sampleAccessibleName(sample: PdfSample): string {
  return `${sample.formName} ${sample.kind} PDF sample`;
}
